import { summarizeCurrencyAmounts } from "./reporting.js";

// Partner rows link to their payment by payment_id (Stripe course and batch
// sales) or by razorpay_order_id (Razorpay course sales). payment_id is exact,
// so it wins; an order id can map to several payments.
export function resolveTransactionCurrencies(rows, payments = []) {
  const byId = new Map(payments.map((payment) => [payment.id, payment]));
  const currencies = new Map();
  for (const payment of payments) {
    if (!payment.razorpay_order_id || !payment.currency) continue;
    const values = currencies.get(payment.razorpay_order_id) || new Set();
    values.add(payment.currency.toUpperCase());
    currencies.set(payment.razorpay_order_id, values);
  }
  return rows.map((row) => {
    const direct = row.payment_id ? byId.get(row.payment_id) : null;
    const linked = currencies.get(row.razorpay_order_id);
    const paymentCurrency = direct?.currency
      ? direct.currency.toUpperCase()
      : linked?.size === 1
        ? [...linked][0]
        : null;
    const conflicting = !paymentCurrency && linked?.size > 1;
    const legacyCurrency = row.team?.branding_settings?.currency;
    const linkedIds = new Set(
      payments
        .filter(
          (payment) =>
            payment.razorpay_order_id &&
            payment.razorpay_order_id === row.razorpay_order_id,
        )
        .map((payment) => payment.id),
    );
    if (row.payment_id) linkedIds.add(row.payment_id);
    return {
      ...row,
      linked_payment_ids: [...linkedIds],
      currency: (
        row.currency ||
        paymentCurrency ||
        (conflicting ? null : legacyCurrency) ||
        "UNKNOWN"
      ).toUpperCase(),
      currency_source: row.currency
        ? "transaction"
        : paymentCurrency
          ? "payment"
          : conflicting
            ? "unknown"
            : legacyCurrency
              ? "legacy_store_setting"
              : "unknown",
    };
  });
}

// balance_owed = earned − paid. Pending payouts are already promised, so the
// amount still free to pay out is available_balance = earned − paid − pending.
// Cancelled payouts count towards neither.
export function buildPartnerCurrencyBalances(partner, transactions, payouts) {
  const earned = summarizeCurrencyAmounts(
    transactions.filter((row) => row.status === "completed"),
    "partner_revenue",
  );
  const paid = summarizeCurrencyAmounts(
    payouts.filter((row) => row.status === "paid"),
  );
  const pending = summarizeCurrencyAmounts(
    payouts.filter((row) => row.status === "pending"),
  );
  return [
    ...new Set([
      ...Object.keys(earned),
      ...Object.keys(paid),
      ...Object.keys(pending),
    ]),
  ].map((currency) => ({
    ...partner,
    currency,
    total_revenue: earned[currency] || 0,
    total_paid: paid[currency] || 0,
    pending_payouts: pending[currency] || 0,
    balance_owed: (earned[currency] || 0) - (paid[currency] || 0),
    available_balance:
      (earned[currency] || 0) -
      (paid[currency] || 0) -
      (pending[currency] || 0),
    legacy_currency: transactions.some(
      (row) =>
        row.currency === currency &&
        row.currency_source === "legacy_store_setting",
    ),
  }));
}

// Validates a new payout against the partner's per-currency balances.
// Whether a payout may exceed the available balance (an advance) is a finance
// decision, so it is allowed only with explicit confirmation.
export function checkPayoutRequest(
  balances,
  { amount, currency, confirmOverBalance = false },
) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) {
    return {
      ok: false,
      code: "PAYOUT_INVALID_AMOUNT",
      message: "Enter an amount greater than zero.",
    };
  }
  const code = String(currency || "")
    .trim()
    .toUpperCase();
  const balance = balances.find(
    (row) => row.currency === code && code !== "UNKNOWN",
  );
  if (!balance) {
    const allowed = balances
      .map((row) => row.currency)
      .filter((c) => c !== "UNKNOWN");
    return {
      ok: false,
      code: "PAYOUT_INVALID_CURRENCY",
      message: allowed.length
        ? `Choose one of this partner's balance currencies: ${allowed.join(", ")}.`
        : "This partner has no balance in a known currency.",
    };
  }
  const available = Math.round(balance.available_balance * 100) / 100;
  if (value > available && !confirmOverBalance) {
    return {
      ok: false,
      code: "PAYOUT_EXCEEDS_AVAILABLE",
      available,
      currency: code,
      message: `This payout exceeds the available ${code} balance of ${available.toFixed(2)}.`,
    };
  }
  return {
    ok: true,
    available,
    currency: code,
    exceedsAvailable: value > available,
  };
}
